import React, { useContext, useMemo } from 'react';
import { ChatDocumentSpec, ChatManager } from 'app/models/chat/documentSpec';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { Stream } from '@engoo/realtime-protocol/stream';
import { ChatState, PendingChatMessage, PersistedChatMessage } from 'app/models/chat/reducer';
import { throwingMethods } from 'app/utils/throwingMethods';
import { IEncodedPersistedAction, StreamPosition } from '@engoo/realtime-protocol/persisted';
import {
  IChatAttachment,
  IChatImageAction,
  IChatMessageAction,
} from '@engoo/realtime-protocol/chat';

import {
  IImageUploadOptions,
  setMessageAttachment as setAttachment,
  setMessageError as setError,
  startImageUpload as startUpload,
  sendMessage as sendChatMessage,
  editMessage as editChatMessage,
  deleteMessage as deleteChatMessage,
} from 'app/models/chat/actions';
import { SyncState } from 'app/models/rtc/sync/constants';
import { List } from 'immutable';
import { IMetadata } from 'app/services/rtc/sync/ActionStream';

export type SetMessageAttachmentAction = (id: StreamPosition, attachment: IChatAttachment) => void;
export type SetMessageErrorAction = (id: number, error: string) => void;
export type StartImageUploadAction = (
  options: IImageUploadOptions,
) => Promise<IChatImageAction & IEncodedPersistedAction>;
export type SendMessageAction = (contents: string) => Promise<IChatMessageAction & IEncodedPersistedAction>;

type EditActionParams = { position: StreamPosition, contents: string };
export type EditMessageAction = (params: EditActionParams) => void;

type DeleteActionParams = { position: StreamPosition };
export type DeleteMessageAction = (params: DeleteActionParams) => void;

export type GetPersistedMessageFn = (position: number) => PersistedChatMessage | undefined;

export interface IChatDocumentContext {
  chatDocumentState: ChatState;
  syncState: SyncState;
  isBlocked: boolean;
  metadata: IMetadata | undefined;
  setMessageAttachment: SetMessageAttachmentAction;
  setMessageError: SetMessageErrorAction;
  startImageUpload: StartImageUploadAction;
  sendMessage: SendMessageAction;
  editMessage: EditMessageAction;
  deleteMessage: DeleteMessageAction;
}

const {
  initialState: documentInitialState,
} = ChatDocumentSpec;

const defaultChatActions = throwingMethods([
  'setMessageAttachment',
  'setMessageError',
  'startImageUpload',
  'sendMessage',
  'editMessage',
  'deleteMessage',
]);

export const defaultChatDocumentContextValue: IChatDocumentContext = {
  chatDocumentState: documentInitialState,
  syncState: SyncState.closed,
  isBlocked: true,
  metadata: undefined,
  ...defaultChatActions,
};

export const ChatDocumentContext = React.createContext<IChatDocumentContext>(
  defaultChatDocumentContextValue,
);

export const useChatContext = () => useContext(ChatDocumentContext);

const useChatActions = (manager: ChatManager | undefined) => {
  const methods = useMemo(() => {
    if (!manager) {
      return defaultChatActions;
    }

    const setMessageAttachment: SetMessageAttachmentAction = (
      id, attachment,
    ) => {
      manager.addLocalAction(setAttachment(id, attachment));
    };

    const setMessageError: SetMessageErrorAction = (id, error) => {
      manager.addLocalAction(setError(id, error));
    };

    const startImageUpload: StartImageUploadAction = (options) => {
      return manager.addLocalActionWithResult(
        startUpload(options),
      );
    };

    const sendMessage: SendMessageAction = (contents) => {
      return manager.addLocalActionWithResult(
        sendChatMessage(contents),
      );
    };

    const editMessage: EditMessageAction = (params) => {
      manager.addLocalAction(
        editChatMessage(params),
      );
    };

    const deleteMessage: DeleteMessageAction = (params) => {
      manager.addLocalAction(
        deleteChatMessage(params),
      );
    };

    return {
      setMessageAttachment,
      setMessageError,
      startImageUpload,
      sendMessage,
      editMessage,
      deleteMessage,
    };
  }, [manager]);

  return methods;
};

export const ChatDocumentProvider: React.FC = ({ children }) => {
  const store = useDocumentManager(Stream.Chat, ChatDocumentSpec);

  const {
    documentState: chatDocumentState = documentInitialState,
    manager,
    isBlocked,
    syncState,
    metadata,
  } = store;

  const actions = useChatActions(manager);

  const contextValue = useMemo(() => ({
    chatDocumentState,
    isBlocked,
    syncState,
    metadata,
    ...actions,
  }), [
    chatDocumentState, isBlocked, actions,
    syncState, metadata,
  ]);

  return (
    <ChatDocumentContext.Provider value={contextValue}>
      {children}
    </ChatDocumentContext.Provider>
  );
};

export const useAllMessages = (
  chatDocumentState: ChatState,
) => {
  return useMemo(() => {
    const persisted: List<PendingChatMessage | PersistedChatMessage> = chatDocumentState.persisted.toList();

    return persisted.concat(chatDocumentState.pending.toList());
  }, [chatDocumentState.persisted, chatDocumentState.pending]);
};
